import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, AlertTriangle, Scale, FileCheck, Lock, Gavel } from 'lucide-react';

const TermsPage = () => {
  const language = localStorage.getItem('language') || 'en';
  
  const content = {
    en: {
      title: "Terms of Service & Privacy Policy",
      lastUpdate: "Last updated: May 2026",
      sections: [
        {
          title: "1. Data Collection and Storage",
          icon: "Lock",
          content: `When you use SmartBalloon, we collect and store:
          
• **Technical drawings and logos** you upload to the platform
• **Project data** including markers, dimensions, and measurements
• **Account information** such as email address and subscription status
• **AI usage statistics** for subscription management

All data is stored on secure cloud servers (Supabase) with encryption at rest and in transit. Data centers are located in the European Union.

**GDPR Compliance:**
Personal data processing complies with EU Regulation 2016/679 (GDPR).`
        },
        {
          title: "2. Use of Artificial Intelligence",
          icon: "FileCheck",
          content: `SmartBalloon uses AI services to assist with dimension detection workflows:

**AI Providers:**
• **Artificial Intelligence** - For view-based scanning, vector hybrid detection, and manual click detection

**Important Notice on Data Usage:**
According to OpenAI's enterprise API privacy policy, data submitted via API is not used for public model training.

**Manual Click Detection (AI Marker) - FREE**
• When you click on a dimension, a **cropped region** (400x400px) around your click is sent to the artificial intelligence
• The AI reads the dimension text including special symbols (Ø, ±, M, Ra) and rotated text
• **Does NOT count** toward your monthly AI credit limit

**Vector PDF Detection - 2-3 Credits**
• For **vector PDFs** (CAD-generated), dimensions are extracted directly from the PDF structure using PyMuPDF
• The AI refines the extracted data to filter noise and associate tolerances
• Costs **2 credits** (base) + **1 credit** if AI refinement is used = **2-3 credits total**
• Designed to reduce manual errors for modern CAD drawings

**View-Based AI Detection - 2 Credits per View**
• You define rectangular **Views** on your drawing by clicking and dragging
• Each view is scanned with **artificial intelligence at high resolution** to detect dimensions
• Costs approximately **2 credits per view**
• Results should always be verified by qualified personnel

**Company Logo**
• You can upload your company logo from the Dashboard
• The logo will appear on exported ISIR reports
• Maximum size: 500KB (PNG, JPG, SVG)

By using AI features, you consent to data processing by OpenAI.`
        },
        {
          title: "3. Subscription Plans & AI Usage",
          icon: "Scale",
          content: `**AI Plan (€99.99/month + VAT)**
• Up to **50 AI credits** per month
• Vector PDF detection: **2-3 credits** per scan
• View-based detection: **~2 credits** per view
• SmartRevision basic included
• ~20 scans can be performed per month
• Manual click readings are **FREE** and unlimited

**AI Unlimited* Plan (€119.99/month + VAT)**
• **Unlimited AI credits***
• Scan as many views/drawings as needed
• Unlimited SmartRevision
• Priority support

*Subject to Fair Use Policy:
• Intended for reasonable commercial use
• Automated mass processing or abuse prevention measures apply
• Excessive automated usage may be subject to limitations
• We reserve the right to contact users about abnormal usage patterns

Manual click readings (single dimension) do **not** count toward your monthly AI credit limit.`
        },
        {
          title: "4. SmartRevision (Drawing Comparison)",
          icon: "FileCheck",
          content: `**SmartRevision** allows you to compare two revisions of a technical drawing to identify differences.

**How it works:**
• Upload Drawing A (previous revision) and Drawing B (new revision)
• The system aligns the drawings automatically
• OpenCV detects visual differences
• Artificial intelligence analyzes semantic differences (dimensions, holes, tolerances)

**Output:**
• Color-coded overlay: **Red** = Removed, **Green** = Added, **Yellow** = Modified
• List of semantic differences with descriptions
• Exportable PDF report with legend and summary

**Cost:** 10 AI credits per automatic analysis

**GUIDED VIEW COMPARISON (Advanced):**
This feature is designed for drawings with **changed layouts** between revisions.

**Cost:** 2 AI credits per comparison view

**SMARTREVISION DISCLAIMER:**
• L.S.C. s.r.l. is **NOT responsible** for any malfunctions, inaccuracies, or inefficiencies of SmartRevision
• L.S.C. s.r.l. is **NOT responsible** for any consequences arising from the use of SmartRevision results
• **Human supervision is MANDATORY** before proceeding with production or quality processes
• All results must be verified by qualified technical personnel`
        },
        {
          title: "5. Your Responsibilities",
          icon: "Shield",
          content: `By uploading content to SmartBalloon, you confirm that:

• You have the **legal rights** to upload and process the drawings
• The drawings do not contain **confidential information** that cannot be processed by third-party cloud/AI services
• You will not upload content that violates intellectual property rights of others

**NDA and Confidential Data Notice:**
The user is responsible for verifying that uploaded drawings can be processed through cloud infrastructure and third-party AI services according to any applicable NDAs, corporate policies, or contractual restrictions.

**Company Logo Upload:**
• By uploading a company logo, you declare that you have the **legal rights** to use and display said logo
• You are solely responsible for the proper use of the uploaded logo
• **L.S.C. s.r.l. and SmartBalloon decline any responsibility** for improper, unauthorized, or illegal use of uploaded logos or technical drawings`
        },
        {
          title: "6. Mandatory Human Verification",
          icon: "AlertTriangle",
          content: `**IMPORTANT: ALL RESULTS MUST BE VERIFIED BY QUALIFIED PERSONNEL**

All results generated by SmartBalloon and SmartRevision must be verified by qualified technical personnel before use in:

• Production processes
• Quality inspections and testing
• Certification procedures
• Official documentation
• Regulatory compliance activities
• Customer deliverables

**SmartBalloon is an AI-assisted productivity tool designed to reduce manual errors, NOT a replacement for human expertise and verification.**`
        },
        {
          title: "7. No Warranties Disclaimer",
          icon: "AlertTriangle",
          content: `**IMPORTANT NOTICE:**

SmartBalloon and SmartRevision software is provided "AS IS" and "AS AVAILABLE", without express or implied warranties of accuracy, continuity, reliability, or fitness for a specific purpose.

**L.S.C. s.r.l. does NOT guarantee:**
• Absence of errors or defects
• Continuous or uninterrupted service availability
• Correctness or completeness of AI-generated results
• Compatibility with all CAD/PDF/CMM formats
• Absence of false positives, omissions, or incomplete detections
• Accuracy of dimension readings, tolerances, or technical interpretations

**The software is an operational support tool and NOT a certified automatic validation system.**

**NOT A CERTIFIED SYSTEM:**
SmartBalloon and SmartRevision do NOT constitute certified systems for metrological validation, technical approval, or regulatory compliance.`
        },
        {
          title: "8. Limitation of Liability",
          icon: "Scale",
          content: `**To the maximum extent permitted by applicable law:**

L.S.C. s.r.l. shall NOT be held liable for:
• Production rejects or scrap
• Rework or repair costs
• Production downtime or delays
• Non-conformities or quality defects
• Product recalls
• Customer complaints or disputes
• Quality audit failures
• Economic or financial losses
• Dimensional or interpretive errors
• Consequential, incidental, or indirect damages

arising directly or indirectly from the use of the software or generated results.

• The service is provided "AS IS" without warranties of any kind
• Total liability shall not exceed the amount paid for the service in the 12 months preceding the claim

**By using SmartBalloon, you acknowledge and accept these limitations.**`
        },
        {
          title: "9. Beta Features",
          icon: "AlertTriangle",
          content: `Some features may be released in beta or experimental mode and may be subject to changes, limitations, or discontinuation without prior notice.

Beta features are provided for testing purposes and may not be suitable for production use.`
        },
        {
          title: "10. AI Chatbot Disclaimer (DenAIs)",
          icon: "Shield",
          content: `SmartBalloon includes an AI-powered chatbot assistant named **"DenAIs"** for user support.

**Important disclaimers:**
• DenAIs is an **artificial intelligence system** and is **NOT connected to any real person**
• The name "DenAIs" is purely fictional and has no reference to real individuals
• Responses provided by DenAIs are for informational purposes only
• L.S.C. s.r.l. is not responsible for any actions taken based on DenAIs responses`
        },
        {
          title: "11. Data Retention",
          icon: "Lock",
          content: `• Your projects and drawings are stored for as long as your account is active
• Upon account deletion, all associated data is permanently removed within 30 days
• You can delete individual projects at any time from your dashboard`
        },
        {
          title: "12. Security Measures",
          icon: "Lock",
          content: `We implement industry-standard security measures:

• HTTPS encryption for all data transfers
• Encrypted database storage
• Secure authentication via Supabase Auth
• Regular security audits`
        },
        {
          title: "13. Subscription Terms",
          icon: "Scale",
          content: `**Billing:**
• Subscriptions are billed monthly
• Payments are processed securely via Stripe
• You can cancel anytime; access continues until the end of the billing period

**Refunds:**
• Refund requests are handled on a case-by-case basis
• Contact info@lscsrl.it for billing inquiries`
        },
        {
          title: "14. Jurisdiction",
          icon: "Gavel",
          content: `For any dispute relating to the use of this service, the **Court of Bologna, Italy** shall have exclusive jurisdiction.

This agreement is governed by Italian law.`
        },
        {
          title: "15. Contact",
          icon: "Shield",
          content: `For questions about these terms or privacy, contact us at: **info@lscsrl.it**

L.S.C. s.r.l.
Bologna, Italy`
        }
      ]
    },
    it: {
      title: "Termini di Servizio e Privacy Policy",
      lastUpdate: "Ultimo aggiornamento: Maggio 2026",
      sections: [
        {
          title: "1. Raccolta e Archiviazione Dati",
          icon: "Lock",
          content: `Quando utilizzi SmartBalloon, raccogliamo e archiviamo:
          
• **Disegni tecnici e loghi** che carichi sulla piattaforma
• **Dati dei progetti** inclusi marker, quote e misurazioni
• **Informazioni account** come indirizzo email e stato abbonamento
• **Statistiche utilizzo AI** per la gestione dell'abbonamento

Tutti i dati sono archiviati su server cloud sicuri (Supabase) con crittografia a riposo e in transito. I data center si trovano nell'Unione Europea.

**Conformità GDPR:**
Il trattamento dei dati personali avviene nel rispetto del Regolamento UE 2016/679 (GDPR).`
        },
        {
          title: "2. Utilizzo dell'Intelligenza Artificiale",
          icon: "FileCheck",
          content: `SmartBalloon utilizza servizi AI per assistere nei workflow di rilevamento quote:

**Fornitori AI:**
• **Intelligenza Artificiale** - Per la scansione per viste, rilevamento vettoriale ibrido e lettura click manuale

**Nota Importante sull'Utilizzo dei Dati:**
Secondo quanto dichiarato dal fornitore OpenAI per i servizi API enterprise, i dati inviati tramite API non vengono utilizzati per l'addestramento pubblico dei modelli.

**Lettura Click Manuale (Marker AI) - GRATUITA**
• Quando clicchi su una quota, una **porzione ritagliata** (400x400px) intorno al click viene inviata all'intelligenza artificiale
• L'AI legge il testo della quota inclusi simboli speciali (Ø, ±, M, Ra) e testo ruotato
• **NON conta** verso il tuo limite mensile di crediti AI

**Rilevamento PDF Vettoriale - 2-3 Crediti**
• Per **PDF vettoriali** (generati da CAD), le quote vengono estratte direttamente dalla struttura del PDF usando PyMuPDF
• L'IA raffina i dati estratti per filtrare il rumore e associare le tolleranze
• Costa **2 crediti** (base) + **1 credito** se viene usato il raffinamento AI = **2-3 crediti totali**
• Progettato per ridurre gli errori manuali nei disegni CAD moderni

**Scansione AI per Viste - 2 Crediti per Vista**
• Definisci delle **Viste** rettangolari sul tuo disegno cliccando e trascinando
• Ogni vista viene scansionata con **l'intelligenza artificiale ad alta risoluzione** per rilevare le quote
• Costa circa **2 crediti per vista**
• I risultati devono sempre essere verificati da personale qualificato

**Logo Aziendale**
• Puoi caricare il logo della tua azienda dalla Dashboard
• Il logo apparirà nei report ISIR esportati
• Dimensione massima: 500KB (PNG, JPG, SVG)

Utilizzando le funzionalità AI, acconsenti all'elaborazione dei dati da parte di OpenAI.`
        },
        {
          title: "3. Piani di Abbonamento e Utilizzo AI",
          icon: "Scale",
          content: `**Piano AI (€99,99/mese + IVA)**
• Fino a **50 crediti AI** al mese
• Rilevamento PDF vettoriale: **2-3 crediti** per scansione
• Rilevamento per viste: **~2 crediti** per vista
• SmartRevision base incluso
• ~20 scansioni possono essere effettuate al mese
• Le letture click manuali sono **GRATUITE** e illimitate

**Piano AI Unlimited* (€119,99/mese + IVA)**
• **Crediti AI illimitati***
• Scansiona tutte le viste/disegni necessari
• SmartRevision illimitato
• Supporto prioritario

*Soggetto a Fair Use Policy:
• Destinato a un utilizzo commerciale ragionevole
• Si applicano misure di prevenzione abusi e utilizzo automatizzato massivo
• L'utilizzo automatizzato eccessivo potrebbe essere soggetto a limitazioni
• Ci riserviamo il diritto di contattare gli utenti in caso di pattern di utilizzo anomali

Le letture click manuali (singola quota) **NON** contano verso il limite mensile di crediti AI.`
        },
        {
          title: "4. SmartRevision (Comparazione Disegni)",
          icon: "FileCheck",
          content: `**SmartRevision** permette di confrontare due revisioni di un disegno tecnico per identificare le differenze.

**Come funziona:**
• Carica Disegno A (revisione precedente) e Disegno B (nuova revisione)
• Il sistema allinea automaticamente i disegni
• OpenCV rileva le differenze visive
• L'intelligenza artificiale analizza le differenze semantiche (quote, fori, tolleranze)

**Output:**
• Overlay a colori: **Rosso** = Rimosso, **Verde** = Aggiunto, **Giallo** = Modificato
• Lista delle differenze semantiche con descrizioni
• Report PDF esportabile con legenda e riepilogo

**Costo:** 10 crediti AI per analisi automatica

**COMPARAZIONE GUIDATA (Avanzata):**
Questa funzionalità è progettata per disegni con **layout modificati** tra le revisioni.

**Costo:** 2 crediti AI per vista confrontata

**DISCLAIMER SMARTREVISION:**
• L.S.C. s.r.l. **NON è responsabile** per eventuali malfunzionamenti, inesattezze o inefficienze di SmartRevision
• L.S.C. s.r.l. **NON è responsabile** per qualsiasi conseguenza derivante dall'utilizzo dei risultati di SmartRevision
• **La supervisione umana è OBBLIGATORIA** prima di procedere con processi produttivi o di qualità
• Tutti i risultati devono essere verificati da personale tecnico qualificato`
        },
        {
          title: "5. Responsabilità dell'Utente",
          icon: "Shield",
          content: `Caricando contenuti su SmartBalloon, confermi che:

• Hai i **diritti legali** per caricare e processare i disegni
• I disegni non contengono **informazioni riservate** che non possono essere processate da servizi cloud/AI di terze parti
• Non caricherai contenuti che violano i diritti di proprietà intellettuale di altri

**Avviso NDA e Dati Riservati:**
L'utente è responsabile di verificare che i disegni caricati possano essere trattati tramite infrastrutture cloud e servizi AI di terze parti secondo eventuali NDA, policy aziendali o vincoli contrattuali applicabili.

**Caricamento Logo Aziendale:**
• Caricando un logo aziendale, dichiari di avere i **diritti legali** per usare e mostrare tale logo
• Sei l'unico responsabile dell'uso corretto del logo caricato
• **L.S.C. s.r.l. e SmartBalloon declinano ogni responsabilità** per uso improprio, non autorizzato o illegale di loghi caricati o disegni tecnici`
        },
        {
          title: "6. Obbligo di Verifica Umana",
          icon: "AlertTriangle",
          content: `**IMPORTANTE: TUTTI I RISULTATI DEVONO ESSERE VERIFICATI DA PERSONALE QUALIFICATO**

Tutti i risultati generati da SmartBalloon e SmartRevision devono essere verificati da personale tecnico qualificato prima dell'utilizzo in:

• Processi produttivi
• Ispezioni e collaudi qualità
• Procedure di certificazione
• Documentazione ufficiale
• Attività di conformità normativa
• Deliverable per clienti

**SmartBalloon è uno strumento di produttività assistito da AI progettato per ridurre gli errori manuali, NON un sostituto della competenza e verifica umana.**`
        },
        {
          title: "7. Assenza di Garanzie",
          icon: "AlertTriangle",
          content: `**AVVISO IMPORTANTE:**

Il software SmartBalloon e SmartRevision viene fornito "COSÌ COM'È" e "SECONDO DISPONIBILITÀ", senza garanzie espresse o implicite di accuratezza, continuità, affidabilità o idoneità per uno scopo specifico.

**L.S.C. s.r.l. NON garantisce:**
• Assenza di errori o difetti
• Disponibilità continua o ininterrotta del servizio
• Correttezza o completezza dei risultati generati dall'AI
• Compatibilità con tutti i formati CAD/PDF/CMM
• Assenza di falsi positivi, omissioni o rilevamenti incompleti
• Accuratezza delle letture dimensionali, tolleranze o interpretazioni tecniche

**Il software costituisce uno strumento di supporto operativo e NON un sistema certificato di validazione automatica.**

**NON È UN SISTEMA CERTIFICATO:**
SmartBalloon e SmartRevision non costituiscono sistemi certificati di validazione metrologica, approvazione tecnica o conformità normativa.`
        },
        {
          title: "8. Limitazione di Responsabilità",
          icon: "Scale",
          content: `**Nella misura massima consentita dalla legge applicabile:**

L.S.C. s.r.l. NON potrà essere ritenuta responsabile per:
• Scarti produttivi
• Costi di rilavorazione o riparazione
• Fermo produzione o ritardi
• Non conformità o difetti qualitativi
• Richiami prodotto
• Contestazioni o reclami cliente
• Fallimenti audit qualità
• Perdite economiche o finanziarie
• Errori dimensionali o interpretativi
• Danni consequenziali, incidentali o indiretti

derivanti direttamente o indirettamente dall'utilizzo del software o dei risultati generati.

• Il servizio è fornito "COSÌ COM'È" senza garanzie di alcun tipo
• La responsabilità totale non supererà l'importo pagato per il servizio nei 12 mesi precedenti il reclamo

**Utilizzando SmartBalloon, riconosci e accetti queste limitazioni.**`
        },
        {
          title: "9. Funzionalità Beta",
          icon: "AlertTriangle",
          content: `Alcune funzionalità possono essere rilasciate in modalità beta o sperimentale e potrebbero subire modifiche, limitazioni o interruzioni senza preavviso.

Le funzionalità beta sono fornite a scopo di test e potrebbero non essere adatte per l'uso in produzione.`
        },
        {
          title: "10. Disclaimer Chatbot AI (DenAIs)",
          icon: "Shield",
          content: `SmartBalloon include un assistente chatbot alimentato da AI chiamato **"DenAIs"** per il supporto utenti.

**Disclaimer importanti:**
• DenAIs è un **sistema di intelligenza artificiale** e **NON è collegato a nessuna persona reale**
• Il nome "DenAIs" è puramente fittizio e non ha riferimenti a individui reali
• Le risposte fornite da DenAIs sono solo a scopo informativo
• L.S.C. s.r.l. non è responsabile per qualsiasi azione intrapresa in base alle risposte di DenAIs`
        },
        {
          title: "11. Conservazione dei Dati",
          icon: "Lock",
          content: `• I tuoi progetti e disegni sono archiviati finché il tuo account è attivo
• Alla cancellazione dell'account, tutti i dati associati vengono rimossi permanentemente entro 30 giorni
• Puoi eliminare singoli progetti in qualsiasi momento dalla dashboard`
        },
        {
          title: "12. Misure di Sicurezza",
          icon: "Lock",
          content: `Implementiamo misure di sicurezza standard del settore:

• Crittografia HTTPS per tutti i trasferimenti dati
• Archiviazione database crittografata
• Autenticazione sicura tramite Supabase Auth
• Audit di sicurezza regolari`
        },
        {
          title: "13. Termini di Abbonamento",
          icon: "Scale",
          content: `**Fatturazione:**
• Gli abbonamenti vengono fatturati mensilmente
• I pagamenti sono processati in modo sicuro tramite Stripe
• Puoi annullare in qualsiasi momento; l'accesso continua fino alla fine del periodo di fatturazione

**Rimborsi:**
• Le richieste di rimborso vengono gestite caso per caso
• Contatta info@lscsrl.it per domande sulla fatturazione`
        },
        {
          title: "14. Foro Competente",
          icon: "Gavel",
          content: `Per qualsiasi controversia relativa all'utilizzo del servizio sarà competente in via esclusiva il **Foro di Bologna**.

Il presente accordo è regolato dalla legge italiana.`
        },
        {
          title: "15. Contatti",
          icon: "Shield",
          content: `Per domande su questi termini o sulla privacy, contattaci a: **info@lscsrl.it**

L.S.C. s.r.l.
Bologna, Italia`
        }
      ]
    },
    de: {
      title: "Nutzungsbedingungen & Datenschutzerklärung",
      lastUpdate: "Letzte Aktualisierung: Mai 2026",
      sections: [
        {
          title: "1. Datenerhebung und -speicherung",
          icon: "Lock",
          content: `Bei der Nutzung von SmartBalloon erheben und speichern wir:
          
• **Technische Zeichnungen und Logos**, die Sie auf die Plattform hochladen
• **Projektdaten** einschließlich Marker, Maße und Messungen
• **Kontoinformationen** wie E-Mail-Adresse und Abonnementstatus
• **KI-Nutzungsstatistiken** für die Abonnementverwaltung

Alle Daten werden auf sicheren Cloud-Servern (Supabase) mit Verschlüsselung gespeichert. Die Rechenzentren befinden sich in der Europäischen Union.

**DSGVO-Konformität:**
Die Verarbeitung personenbezogener Daten erfolgt gemäß der EU-Verordnung 2016/679 (DSGVO).`
        },
        {
          title: "2. Nutzung Künstlicher Intelligenz",
          icon: "FileCheck",
          content: `SmartBalloon nutzt KI-Dienste zur Unterstützung bei der Maßerkennung:

**KI-Anbieter:**
• **Künstliche Intelligenz** - Für ansichtsbasiertes Scannen, hybride Vektorerkennung und manuelle Klickerkennung

**Wichtiger Hinweis zur Datennutzung:**
Laut den Enterprise-API-Datenschutzrichtlinien von OpenAI werden über API übermittelte Daten nicht für das öffentliche Modelltraining verwendet.

**Manuelle Klickerkennung (KI-Marker) - KOSTENLOS**
• Wenn Sie auf ein Maß klicken, wird ein **zugeschnittener Bereich** (400x400px) an die künstliche Intelligenz gesendet
• Die KI liest den Maßtext einschließlich Sonderzeichen (Ø, ±, M, Ra)
• Zählt **NICHT** zu Ihrem monatlichen KI-Kreditlimit

**Entwickelt um manuelle Fehler zu reduzieren** bei modernen CAD-Zeichnungen.

Mit der Nutzung der KI-Funktionen stimmen Sie der Datenverarbeitung durch OpenAI zu.`
        },
        {
          title: "3. Abonnementpläne & KI-Nutzung",
          icon: "Scale",
          content: `**KI-Plan (€99,99/Monat + MwSt.)**
• Bis zu **50 KI-Credits** pro Monat
• Vektor-PDF-Erkennung: **2-3 Credits** pro Scan
• Ansichtsbasierte Erkennung: **~2 Credits** pro Ansicht
• SmartRevision Basis enthalten
• Manuelle Klicklesungen sind **KOSTENLOS** und unbegrenzt

**KI Unlimited* Plan (€119,99/Monat + MwSt.)**
• **Unbegrenzte KI-Credits***
• Scannen Sie so viele Ansichten/Zeichnungen wie nötig
• Unbegrenztes SmartRevision
• Prioritätssupport

*Unterliegt der Fair-Use-Richtlinie:
• Für angemessene kommerzielle Nutzung vorgesehen
• Maßnahmen zur Missbrauchsprävention gelten
• Übermäßige automatisierte Nutzung kann eingeschränkt werden`
        },
        {
          title: "4. Pflicht zur menschlichen Überprüfung",
          icon: "AlertTriangle",
          content: `**WICHTIG: ALLE ERGEBNISSE MÜSSEN VON QUALIFIZIERTEM PERSONAL ÜBERPRÜFT WERDEN**

Alle von SmartBalloon und SmartRevision generierten Ergebnisse müssen von qualifiziertem technischem Personal überprüft werden vor der Verwendung in:

• Produktionsprozessen
• Qualitätsprüfungen und Tests
• Zertifizierungsverfahren
• Offizieller Dokumentation
• Regulatorischen Compliance-Aktivitäten

**SmartBalloon ist ein KI-gestütztes Produktivitätswerkzeug zur Reduzierung manueller Fehler, KEIN Ersatz für menschliche Expertise und Überprüfung.**`
        },
        {
          title: "5. Gewährleistungsausschluss",
          icon: "AlertTriangle",
          content: `**WICHTIGER HINWEIS:**

Die Software SmartBalloon und SmartRevision wird "WIE BESEHEN" und "NACH VERFÜGBARKEIT" bereitgestellt, ohne ausdrückliche oder stillschweigende Garantien für Genauigkeit, Kontinuität, Zuverlässigkeit oder Eignung für einen bestimmten Zweck.

**L.S.C. s.r.l. garantiert NICHT:**
• Fehlerfreiheit
• Kontinuierliche Serviceverfügbarkeit
• Richtigkeit der KI-generierten Ergebnisse
• Kompatibilität mit allen CAD/PDF/CMM-Formaten

**Die Software ist ein operatives Unterstützungswerkzeug und KEIN zertifiziertes automatisches Validierungssystem.**`
        },
        {
          title: "6. Haftungsbeschränkung",
          icon: "Scale",
          content: `**Im maximal gesetzlich zulässigen Umfang:**

L.S.C. s.r.l. haftet NICHT für:
• Produktionsausschuss
• Nacharbeitskosten
• Produktionsstillstand
• Qualitätsmängel
• Produktrückrufe
• Kundenbeschwerden
• Wirtschaftliche Verluste
• Maß- oder Interpretationsfehler

die direkt oder indirekt aus der Nutzung der Software entstehen.

**Mit der Nutzung von SmartBalloon erkennen Sie diese Einschränkungen an.**`
        },
        {
          title: "7. Gerichtsstand",
          icon: "Gavel",
          content: `Für alle Streitigkeiten bezüglich der Nutzung dieses Dienstes ist ausschließlich das **Gericht von Bologna, Italien** zuständig.

Diese Vereinbarung unterliegt italienischem Recht.`
        },
        {
          title: "8. Kontakt",
          icon: "Shield",
          content: `Bei Fragen zu diesen Bedingungen oder zum Datenschutz kontaktieren Sie uns unter: **info@lscsrl.it**

L.S.C. s.r.l.
Bologna, Italien`
        }
      ]
    },
    fr: {
      title: "Conditions d'Utilisation & Politique de Confidentialité",
      lastUpdate: "Dernière mise à jour: Mai 2026",
      sections: [
        {
          title: "1. Collecte et Stockage des Données",
          icon: "Lock",
          content: `Lorsque vous utilisez SmartBalloon, nous collectons et stockons:
          
• **Dessins techniques et logos** que vous téléchargez sur la plateforme
• **Données de projet** incluant marqueurs, dimensions et mesures
• **Informations de compte** comme l'adresse e-mail et le statut d'abonnement
• **Statistiques d'utilisation IA** pour la gestion des abonnements

Toutes les données sont stockées sur des serveurs cloud sécurisés (Supabase) avec chiffrement. Les centres de données sont situés dans l'Union Européenne.

**Conformité RGPD:**
Le traitement des données personnelles est conforme au Règlement UE 2016/679 (RGPD).`
        },
        {
          title: "2. Utilisation de l'Intelligence Artificielle",
          icon: "FileCheck",
          content: `SmartBalloon utilise des services IA pour assister dans les workflows de détection de dimensions:

**Fournisseurs IA:**
• **Intelligence Artificielle** - Pour le scan par vues, la détection vectorielle hybride et la détection par clic manuel

**Avis Important sur l'Utilisation des Données:**
Selon la politique de confidentialité d'OpenAI pour les services API enterprise, les données soumises via API ne sont pas utilisées pour l'entraînement public des modèles.

**Détection par Clic Manuel (Marqueur IA) - GRATUIT**
• Lorsque vous cliquez sur une dimension, une **région recadrée** (400x400px) est envoyée à l'intelligence artificielle
• L'IA lit le texte de dimension incluant les symboles spéciaux (Ø, ±, M, Ra)
• **Ne compte PAS** dans votre limite mensuelle de crédits IA

**Conçu pour réduire les erreurs manuelles** sur les dessins CAO modernes.

En utilisant les fonctionnalités IA, vous consentez au traitement des données par OpenAI.`
        },
        {
          title: "3. Plans d'Abonnement & Utilisation IA",
          icon: "Scale",
          content: `**Plan IA (99,99€/mois + TVA)**
• Jusqu'à **50 crédits IA** par mois
• Détection PDF vectoriel: **2-3 crédits** par scan
• Détection par vues: **~2 crédits** par vue
• SmartRevision basique inclus
• Les lectures par clic manuel sont **GRATUITES** et illimitées

**Plan IA Unlimited* (119,99€/mois + TVA)**
• **Crédits IA illimités***
• Scannez autant de vues/dessins que nécessaire
• SmartRevision illimité
• Support prioritaire

*Soumis à la Politique d'Utilisation Équitable:
• Destiné à un usage commercial raisonnable
• Des mesures de prévention des abus s'appliquent
• L'utilisation automatisée excessive peut être limitée`
        },
        {
          title: "4. Obligation de Vérification Humaine",
          icon: "AlertTriangle",
          content: `**IMPORTANT: TOUS LES RÉSULTATS DOIVENT ÊTRE VÉRIFIÉS PAR DU PERSONNEL QUALIFIÉ**

Tous les résultats générés par SmartBalloon et SmartRevision doivent être vérifiés par du personnel technique qualifié avant utilisation dans:

• Processus de production
• Inspections et tests qualité
• Procédures de certification
• Documentation officielle
• Activités de conformité réglementaire

**SmartBalloon est un outil de productivité assisté par IA conçu pour réduire les erreurs manuelles, PAS un remplacement de l'expertise et de la vérification humaine.**`
        },
        {
          title: "5. Absence de Garanties",
          icon: "AlertTriangle",
          content: `**AVIS IMPORTANT:**

Le logiciel SmartBalloon et SmartRevision est fourni "EN L'ÉTAT" et "SELON DISPONIBILITÉ", sans garanties expresses ou implicites d'exactitude, de continuité, de fiabilité ou d'adéquation à un usage spécifique.

**L.S.C. s.r.l. NE garantit PAS:**
• L'absence d'erreurs ou de défauts
• La disponibilité continue du service
• L'exactitude des résultats générés par l'IA
• La compatibilité avec tous les formats CAO/PDF/CMM

**Le logiciel est un outil de support opérationnel et NON un système de validation automatique certifié.**`
        },
        {
          title: "6. Limitation de Responsabilité",
          icon: "Scale",
          content: `**Dans la mesure maximale permise par la loi applicable:**

L.S.C. s.r.l. ne pourra PAS être tenue responsable pour:
• Rebuts de production
• Coûts de reprise
• Arrêts de production
• Non-conformités
• Rappels de produits
• Réclamations clients
• Pertes économiques
• Erreurs dimensionnelles ou d'interprétation

résultant directement ou indirectement de l'utilisation du logiciel.

**En utilisant SmartBalloon, vous reconnaissez ces limitations.**`
        },
        {
          title: "7. Juridiction",
          icon: "Gavel",
          content: `Pour tout litige relatif à l'utilisation de ce service, le **Tribunal de Bologne, Italie** aura compétence exclusive.

Cet accord est régi par le droit italien.`
        },
        {
          title: "8. Contact",
          icon: "Shield",
          content: `Pour toute question concernant ces conditions ou la confidentialité, contactez-nous à: **info@lscsrl.it**

L.S.C. s.r.l.
Bologne, Italie`
        }
      ]
    }
  };

  const currentContent = content[language] || content.en;
  
  const getIcon = (iconName) => {
    switch(iconName) {
      case 'Lock': return <Lock className="w-5 h-5" />;
      case 'Shield': return <Shield className="w-5 h-5" />;
      case 'AlertTriangle': return <AlertTriangle className="w-5 h-5" />;
      case 'Scale': return <Scale className="w-5 h-5" />;
      case 'FileCheck': return <FileCheck className="w-5 h-5" />;
      case 'Gavel': return <Gavel className="w-5 h-5" />;
      default: return <Shield className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Back button */}
        <Link to="/login">
          <Button variant="ghost" className="mb-6 -ml-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'it' ? 'Torna al Login' : language === 'de' ? 'Zurück zum Login' : language === 'fr' ? 'Retour à la connexion' : 'Back to Login'}
          </Button>
        </Link>

        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-3">
            {currentContent.title}
          </h1>
          <p className="text-sm text-slate-500">
            {currentContent.lastUpdate}
          </p>
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800 font-medium">
              {language === 'it' 
                ? '⚠️ Leggere attentamente tutti i termini prima di utilizzare il servizio'
                : language === 'de'
                  ? '⚠️ Bitte lesen Sie alle Bedingungen sorgfältig durch, bevor Sie den Dienst nutzen'
                  : language === 'fr'
                    ? '⚠️ Veuillez lire attentivement toutes les conditions avant d\'utiliser le service'
                    : '⚠️ Please read all terms carefully before using the service'}
            </p>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {currentContent.sections.map((section, index) => (
            <section 
              key={index} 
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
            >
              <div className="flex items-center gap-3 px-6 py-4 bg-slate-50 border-b border-slate-200">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  {getIcon(section.icon)}
                </div>
                <h2 className="text-lg font-bold text-slate-900">
                  {section.title}
                </h2>
              </div>
              <div className="px-6 py-5">
                <div 
                  className="prose prose-slate prose-sm max-w-none
                    prose-p:text-slate-600 prose-p:leading-relaxed
                    prose-strong:text-slate-800 prose-strong:font-semibold
                    prose-ul:text-slate-600 prose-ul:my-3
                    prose-li:my-1 prose-li:marker:text-slate-400"
                  style={{ whiteSpace: 'pre-line' }}
                >
                  {section.content.split('\n').map((line, i) => {
                    // Handle bullet points
                    if (line.trim().startsWith('•')) {
                      return (
                        <p key={i} className="flex items-start gap-2 my-1 text-sm">
                          <span className="text-slate-400 mt-0.5">•</span>
                          <span dangerouslySetInnerHTML={{ 
                            __html: line.substring(1).trim()
                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          }} />
                        </p>
                      );
                    }
                    // Handle headers with **
                    if (line.trim().startsWith('**') && line.trim().endsWith('**')) {
                      return (
                        <p key={i} className="font-semibold text-slate-800 mt-4 mb-2 text-sm">
                          {line.replace(/\*\*/g, '')}
                        </p>
                      );
                    }
                    // Regular text with bold formatting
                    if (line.trim()) {
                      return (
                        <p key={i} className="text-sm text-slate-600 my-2" dangerouslySetInnerHTML={{ 
                          __html: line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-800">$1</strong>')
                        }} />
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} L.S.C. s.r.l. - SmartBalloon & SmartRevision
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Bologna, Italy | info@lscsrl.it
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
